import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ServizioMenuService } from 'src/app/shared/services/servizio-menu.service';
import { ArticoloFull } from 'src/app/shared/interfaces/custom/articolo';
import { Link } from 'src/app/shared/interfaces/link';
import { CategoriaAttualeService } from 'src/app/shared/services/custom/categoria-attuale.service';
import { CartService } from 'src/app/shared/services/cart.service';
import { ShopService } from 'src/app/shared/api/shop.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ProductCustomizationComponent } from 'src/app/shared/components/product-customization/product-customization.component';
import { Attributo } from 'src/app/shared/interfaces/custom/attributi';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';


@Component({
  selector: 'app-product-menu-page',
  templateUrl: './product-menu-page.component.html',
  styleUrls: ['./product-menu-page.component.scss'],
})
export class ProductMenuPageComponent implements OnInit {
  product!: ArticoloFull;
  quantity: number = 0;
  breadcrumbs: Link[] = [];
  menuComposizione: ArticoloFull[] = [];

  addingToCart: boolean = false;
  loadingComposizione: boolean = false;
  messaggioErroreComposizione: string | null = null;
  loadingAttributi = false;

  constructor(
    private route: ActivatedRoute,
    private menuService: ServizioMenuService,
    private categoriaService: CategoriaAttualeService,
    public cartService: CartService,
    private router: Router,
    private shopService: ShopService,
    private cd: ChangeDetectorRef,
    private modalService: BsModalService
  ) {}

  ngOnInit(): void {
    this.menuService.getFamiglie();
    this.categoriaService.loadCategorie();

    const codart = this.route.snapshot.paramMap.get('codice');
    if (!codart) return;

    this.menuService.getProductByCode(codart).subscribe({
      next: (response) => {
        if (!response || response.codart !== codart) {
          console.warn('Prodotto non trovato.');
          return;
        }

        this.product = response;
        this.preparaAttributiInline();

        if (this.isBoxProduct(this.product)) {
          this.loadingComposizione = true;
          this.shopService.getArticoliComposizione(this.product.codart).subscribe({
            next: (composizione) => {
              this.menuComposizione = composizione;
            },
            error: (err) => {
              this.messaggioErroreComposizione = err;
              console.error('Errore caricando composizione:', err);
            },
            complete: () => {
              this.loadingComposizione = false;
              this.cd.markForCheck();
            }
          });
        } else {
          this.quantity = this.cartService.getQuantityOfItemInCart(this.product);
        }

        const famiglia = this.product.famiglia;
        if (famiglia) {
          this.categoriaService.getPartialBreadcrumbs([famiglia]).subscribe((links) => {
            const categoryBreadcrumb: Link = {
              label: links.length ? links[0].label : 'Categoria',
              url: `/menu/${famiglia}`
            };
            const productBreadcrumb: Link = {
              label: this.product.descart,
              url: `/menu/product/${this.product.codart}`
            };
            this.breadcrumbs = [categoryBreadcrumb, productBreadcrumb];
          });
        }
      },
      error: (err) => {
        console.error('Errore durante il recupero del prodotto:', err);
      }
    });
  }

  isCustomizable(articolo: ArticoloFull): boolean {
    return articolo.tipo_articolo === 5 ||
           articolo.tipo_articolo === 6 ||
           (articolo.attributi && articolo.attributi.length > 0);
  }

  isBoxProduct(articolo: ArticoloFull): boolean {
    return articolo.tipo_articolo === 5 || articolo.tipo_articolo === 6;
  }

  hasInlineAttributes(): boolean {
    return !this.isBoxProduct(this.product) && !!(this.product?.attributi && this.product.attributi.length);
  }

  canSelectAttributes(): boolean {
    return this.cartService.isCheckoutAvailable();
  }

  getAttributePrice(attr: Attributo): number {
    return Number(attr?.prezzo || 0);
  }

  showAttributePrice(attr: Attributo): boolean {
    return this.getAttributePrice(attr) > 0;
  }

  isAttributeAvailable(attr: Attributo): boolean {
    if (attr.tipo_battuta !== 0) {
      return true;
    }

    if (!attr.articolo) {
      return false;
    }

    for (let i = 1; i <= 4; i++) {
      if (attr.articolo['option_list' + i]) {
        return false;
      }
    }

    if (attr.articolo.acquisti_giacenza_negativa) {
      return true;
    }

    const availableQuantity = Number(attr.articolo.disponibilita_prodotto || 0);
    const inCart = this.cartService.getQuantityOfItemInCart(attr.articolo);
    const requestedQty = this.quantity || 1;
    const alreadySelectedQty = attr.selected ? requestedQty : 0;
    const remainQty = availableQuantity - inCart + alreadySelectedQty - requestedQty;

    return remainQty >= 0;
  }

  getAttributeAvailabilityLabel(attr: Attributo): string {
    if (!this.canSelectAttributes()) {
      return '';
    }

    if (this.loadingAttributi) {
      return 'Verifica disponibilita...';
    }

    if (!this.isAttributeAvailable(attr)) {
      return 'Non disponibile';
    }

    return attr.selected ? 'Selezionato' : 'Disponibile';
  }

  toggleAttribute(attr: Attributo): void {
    if (!this.canSelectAttributes()) {
      return;
    }

    if (!attr.selected && !this.isAttributeAvailable(attr)) {
      return;
    }

    attr.selected = !attr.selected;
  }

  private preparaAttributiInline(): void {
    if (!this.product?.attributi?.length) {
      return;
    }

    const attributiCollegati = this.product.attributi.filter(attr => attr.tipo_battuta === 0 && attr.articolo_collegato);
    if (!attributiCollegati.length) {
      return;
    }

    const richieste = {};
    attributiCollegati.forEach(attr => {
      richieste[attr.articolo_collegato] = this.shopService.getArticolo(attr.articolo_collegato).pipe(
        catchError(() => of(null))
      );
    });

    this.loadingAttributi = true;
    forkJoin(richieste).subscribe({
      next: (articoliCollegati) => {
        attributiCollegati.forEach(attr => {
          attr.articolo = articoliCollegati[attr.articolo_collegato];
        });
      },
      complete: () => {
        this.loadingAttributi = false;
        this.cd.markForCheck();
      }
    });
  }

  onAddToOrder(): void {
    if (this.addingToCart || !this.product) return;

    this.addingToCart = true;

   
    this.shopService.getArticolo(this.product.codart).subscribe({
      next: (articolo) => {
        if (articolo.non_acquistabile) {
          alert('Prodotto disponibile solo in negozio');
          this.addingToCart = false;
          this.cd.markForCheck();
          return;
        }

        if (articolo.tipo_articolo === 5 || articolo.tipo_articolo === 6) {
          this.shopService.getArticoliComposizione(this.product.codart).subscribe({
            next: (composizione) => {
              const modalRef = this.modalService.show(ProductCustomizationComponent, {
                class: 'modal-dialog-centered modal-xl customized-modal-dialog',
                initialState: {
                  articolo: JSON.parse(JSON.stringify(articolo)),
                  qta: this.quantity || 1,
                  articoli: composizione
                }
              });
              modalRef.content.onClose = () => {
                this.quantity = 0;
                this.cd.markForCheck();
              };
              this.addingToCart = false;
              this.cd.markForCheck();
            },
            error: () => {
              this.addingToCart = false;
              this.cd.markForCheck();
              alert('Errore nel caricamento della composizione');
            }
          });
          return;
        } else if (articolo.attributi && articolo.attributi.length) {
          const selectedAttributes = new Set((this.product.attributi || []).filter(attr => attr.selected).map(attr => attr.progressivo));
          articolo.attributi = (articolo.attributi || []).map(attr => ({
            ...attr,
            selected: selectedAttributes.has(attr.progressivo)
          }));

          this.cartService.add(articolo, this.quantity || 1).subscribe({
            complete: () => {
              this.quantity = this.cartService.getQuantityOfItemInCart(articolo);
              this.addingToCart = false;
              this.cd.markForCheck();
            },
            error: (err) => {
              console.error('Errore aggiungendo al carrello:', err);
              this.addingToCart = false;
              this.cd.markForCheck();
            }
          });
          return;
        }

      
        this.cartService.add(articolo, this.quantity || 1).subscribe({
          complete: () => {
            this.quantity = this.cartService.getQuantityOfItemInCart(articolo);
            this.addingToCart = false;
            this.cd.markForCheck();
          },
          error: (err) => {
            console.error('Errore aggiungendo al carrello:', err);
            this.addingToCart = false;
            this.cd.markForCheck();
          }
        });
      },
      error: (err) => {
        console.error('Errore recuperando l\'articolo:', err);
        this.addingToCart = false;
        this.cd.markForCheck();
      }
    });
  }

  checkData(): void {
    this.router.navigate(['/shop/cart/checkout']);
  }

  addProduct(): void {
    this.quantity++;
  }

  removeProduct(): void {
    if (this.quantity > 0) {
      this.quantity--;
    }
  }
}
